// Motore di mapping dell'import generico: funzioni pure, nessuna dipendenza da
// React. Il modale si limita a raccogliere la scelta dell'utente e a chiamare
// `applyMapping`.
import i18next from 'i18next';

/**
 * @typedef {Object} ImportField
 * @property {string} key            Nome del campo di destinazione.
 * @property {string} label          Etichetta mostrata (già tradotta).
 * @property {boolean} [required]    Se true una riga senza valore viene scartata.
 * @property {'string'|'number'|'boolean'} [type]  Coercizione applicata al valore.
 * @property {string[]} [synonyms]   Intestazioni note, per l'auto-mapping.
 * @property {*} [constant]          Valore costante proposto (es. type = 'station').
 * @property {(value:*, row:Object, mapped:Object)=>*} [transform] Trasformazione finale.
 *   `mapped` contiene i campi già risolti (i precedenti nell'ordine dello schema),
 *   così un campo derivato può comporsi dai valori mappati e non dalle chiavi grezze.
 * @property {(value:*, row:Object, mapped:Object)=>string|null} [validate] Errore o null.
 */

/** Normalizza un'intestazione per il confronto fuzzy (accenti, punteggiatura, case). */
export const normalizeHeader = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

/**
 * Propone una mappatura campo → colonna confrontando `key`, `label` e
 * `synonyms` con le intestazioni del file. Prima le corrispondenze esatte, poi
 * quelle per inclusione (`latitudine` ⊃ `lat`).
 *
 * @param {ImportField[]} schema
 * @param {string[]} columns
 * @returns {Record<string, {source?: string, constant?: *}>}
 */
export function guessMapping(schema, columns) {
  const normalized = columns.map((c) => ({ column: c, norm: normalizeHeader(c) }));
  const used = new Set();
  const mapping = {};

  const pick = (candidates, predicate) => {
    for (const cand of candidates) {
      const hit = normalized.find((n) => !used.has(n.column) && predicate(n.norm, cand));
      if (hit) return hit.column;
    }
    return undefined;
  };

  for (const field of schema) {
    const candidates = [field.key, field.label, ...(field.synonyms || [])]
      .filter(Boolean)
      .map(normalizeHeader)
      .filter((c) => c.length > 0);

    const exact = pick(candidates, (norm, cand) => norm === cand);
    const partial =
      exact ??
      pick(
        candidates,
        (norm, cand) => cand.length >= 3 && (norm.startsWith(cand) || cand.startsWith(norm))
      );

    if (partial) {
      used.add(partial);
      mapping[field.key] = { source: partial };
    } else if (field.constant !== undefined) {
      mapping[field.key] = { constant: field.constant };
    } else {
      mapping[field.key] = {};
    }
  }

  return mapping;
}

/** Coercizione di un valore grezzo al tipo dichiarato dal campo. */
function coerce(value, type) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;

  if (type === 'number') {
    // I file italiani usano la virgola come separatore decimale.
    const n = typeof value === 'number' ? value : Number(String(value).trim().replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
  }
  if (type === 'boolean') {
    if (typeof value === 'boolean') return value;
    const v = String(value).trim().toLowerCase();
    if (['1', 'true', 'si', 'sì', 'yes', 'x'].includes(v)) return true;
    if (['0', 'false', 'no', ''].includes(v)) return false;
    return null;
  }
  return typeof value === 'string' ? value.trim() : value;
}

/**
 * Applica la mappatura alle righe grezze.
 *
 * `validIndex[i]` è la posizione in `rows` da cui proviene `valid[i]`: senza di
 * essa una riga valida non è più ricollegabile alla sua origine, e i chiamanti
 * che portano informazioni fuori dallo schema (numero di riga del foglio,
 * blocco di appartenenza) non potrebbero appaiarle.
 *
 * @param {Object[]} rows       Righe come uscite da `parseImportFile`.
 * @param {ImportField[]} schema
 * @param {Record<string, {source?: string, constant?: *}>} mapping
 * @returns {{ valid: Object[], validIndex: number[], invalid: Array<{ index: number, errors: string[], row: Object }> }}
 */
export function applyMapping(rows, schema, mapping) {
  const valid = [];
  const validIndex = [];
  const invalid = [];

  rows.forEach((row, index) => {
    const out = {};
    const errors = [];

    for (const field of schema) {
      const rule = mapping?.[field.key] || {};
      const raw = rule.source !== undefined ? row?.[rule.source] : rule.constant;
      let value = coerce(raw, field.type);

      if (field.type === 'number' && Number.isNaN(value)) {
        errors.push(
          `${field.label}: ${i18next.t('import_errore_numero', { defaultValue: 'valore numerico non valido' })}`
        );
        value = null;
      }

      if (typeof field.transform === 'function') {
        value = field.transform(value, row, out);
      }

      if (field.required && (value === null || value === undefined || value === '')) {
        errors.push(
          `${field.label}: ${i18next.t('import_errore_obbligatorio', { defaultValue: 'obbligatorio' })}`
        );
      }

      if (typeof field.validate === 'function' && value !== null && value !== undefined) {
        const err = field.validate(value, row, out);
        if (err) errors.push(`${field.label}: ${err}`);
      }

      if (value !== null && value !== undefined) out[field.key] = value;
    }

    if (errors.length) {
      invalid.push({ index, errors, row });
    } else {
      valid.push(out);
      validIndex.push(index);
    }
  });

  return { valid, validIndex, invalid };
}
